import { action, makeObservable, observable } from 'mobx'

import {
  CategoryName,
  IPresentationSettings,
  RecentlyUpdatedMode,
  UploadLogFilterType,
  WorkflowsFilterType,
} from '~/client/graph'
import BarState from '~/client/src/desktop/enums/SideBarState'
import ViewModes from '~/client/src/desktop/enums/ViewModes'
import { defaultPresentationScreens } from '~/client/src/desktop/interfaces/IPresentationScreen'
import { IViewState } from '~/client/src/desktop/interfaces/IViewState'
import FieldIds from '~/client/src/shared/enums/DeliveryFieldIds'
import DeliveryGroupingOption from '~/client/src/shared/enums/DeliveryGroupingOption'
import SitemapFilterType from '~/client/src/shared/enums/SitemapFilterType'
import { equipmentResourceDefinition } from '~/client/src/shared/resources/definitions'
import InitialState, {
  IFilters,
  ISharedFilters,
} from '~/client/src/shared/stores/InitialState'
import { safeGetItem } from '~/client/src/shared/utils/safeStorage'

import { AttachmentGroupingOption } from '../../shared/enums/AttachmentsGroupingOption'
import DefaultApps from '../../shared/enums/DefaultApps'
import LogisticsGroupingOption from '../../shared/enums/LogisticsGroupingOption'
import MaterialsGroupingOption from '../../shared/enums/MaterialsGroupingOption'
import MemberFilterType from '../../shared/enums/MemberFilterType'
import ScanStationGroupingOption from '../../shared/enums/ScanStationGroupingOption'
import KnownTranslatorKeys from '../../shared/localization/knownTranslatorKeys'
import UIFilterInfo from '../../shared/stores/substates/UIFilterInfo'
import { copyObjectArray, isEmptyObj } from '../../shared/utils/util'
import FormReportFilterType from '../enums/FormReportFilterType'
import FormReportGroupingOption from '../enums/FormReportGroupingOption'
import GroupingOption from '../enums/GroupingOptions'
import MaterialsFilterType from '../enums/MaterialsFilterType'
import ScanStationFilterType from '../enums/ScanStationFilterType'
import IPresentationDisplayedPage from '../interfaces/IPresentationDisplayedPage'
import { SubscribeUsersSelectorGroupingOption } from '../views/Logistics/components/SubscribeUsersSelector/SubscribeUsersSelector.store'
import AttachmentsFiltersSettingState from './substates/AttachmentsFiltersSettingState'
import DeliveryFiltersSettingState from './substates/DeliveryFiltersSettingState'
import FormReportFiltersSettingState from './substates/FormReportFilterSettingState'
import MaterialsFiltersSettingState from './substates/MaterialsFilterSettingState'
import ScanStationFiltersSettingState from './substates/ScanStationFilterSettingState'

const defaultPresentationInactivityDuration = 3
export interface IBandOption {
  id: string
  name?: string
  nameTraslatorKey?: KnownTranslatorKeys
  shortName?: string
  num?: number
  disabled?: boolean
  bands?: string[]
  deletionIndex?: number
  canDelete?: boolean
}

const defaultFormsViewMode = ViewModes.Calendar
const defaultDeliveriesViewMode = ViewModes.Calendar

export enum SidebarComponentName {
  None = 'None',
  Chat = 'Chat',
  UserDirectory = 'UserDirectory',
}

export default class DesktopInitialState extends InitialState {
  @observable public activeSideBarComponent: SidebarComponentName =
    SidebarComponentName.None
  @observable public appliedCategoryFilters: CategoryName[] = []

  public pushNotificationEntityId = ''
  @observable
  public presentationSettings: IPresentationSettings = {
    screens: defaultPresentationScreens,
    inactivityDuration: defaultPresentationInactivityDuration,
    updatedAt: null,
    updatedBy: null,
    projectId: null,
    id: null,
  }

  @observable public currentPresentationPage: IPresentationDisplayedPage = null

  public materialsList = observable<IViewState>({
    viewMode: ViewModes.List,
    sortingDataKey: null,
  })

  public reportsList = observable<IViewState>({
    viewMode: ViewModes.List,
    sortingDataKey: null,
  })

  public deliveriesList = observable<IViewState>({
    viewMode: defaultDeliveriesViewMode,
    sortingDataKey: null,
  })

  public formsList = observable<IViewState>({
    viewMode:
      (safeGetItem(DefaultApps.formsViewKey) as ViewModes) ||
      defaultFormsViewMode,
    sortingDataKey: null,
  })

  public attachmentsList = observable<IViewState>({
    viewMode: ViewModes.List,
    sortingDataKey: null,
  })

  public filters = observable<IDesktopFilters>({
    searchKey: '',
    selectedRecentlyUpdatedMode: RecentlyUpdatedMode.LastP6Update,
    recentlyUpdatedStartDate: null,
    recentlyUpdatedEndDate: null,
    appliedCustomFilterId: null,
    showEmptyBands: false,
    locationsMap: {},
    selectedBandsOption: {
      id: GroupingOption.None,
      nameTraslatorKey: KnownTranslatorKeys.none,
      num: 0,
      disabled: false,
      bands: [GroupingOption.None],
    },
    selectedDeliveryBandsOption: {
      id: DeliveryGroupingOption[FieldIds.DATE],
      nameTraslatorKey: KnownTranslatorKeys.none,
      num: 0,
      disabled: false,
      bands: [DeliveryGroupingOption[FieldIds.DATE]],
    },
    selectedDeliveryReportBandsOption: {
      id: DeliveryGroupingOption[FieldIds.DATE],
      nameTraslatorKey: KnownTranslatorKeys.none,
      num: 0,
      disabled: false,
      bands: [DeliveryGroupingOption[FieldIds.DATE]],
    },
    selectedMaterialBandsOption: {
      id: MaterialsGroupingOption.NONE,
      nameTraslatorKey: KnownTranslatorKeys.none,
      num: 1,
      disabled: false,
      bands: [MaterialsGroupingOption.NONE],
    },
  })

  public attachmentsFilters = observable<IFilters>({
    searchKey: '',
    groupingKey: AttachmentGroupingOption.None,
    fieldsMap: {
      [UploadLogFilterType.Filetype]: new UIFilterInfo(),
      [UploadLogFilterType.FileExtension]: new UIFilterInfo(),
      [UploadLogFilterType.WorkflowCategory]: new UIFilterInfo(),
      [UploadLogFilterType.Formtype]: new UIFilterInfo(),
      [UploadLogFilterType.Source]: new UIFilterInfo(),
      [UploadLogFilterType.Location]: new UIFilterInfo(),
      [UploadLogFilterType.Company]: new UIFilterInfo(),
      [UploadLogFilterType.UploadedBy]: new UIFilterInfo(),
      [UploadLogFilterType.DateUploaded]: new UIFilterInfo(),
    },
  })

  public equipmentFilters = observable<IFilters>({
    searchKey: '',
    groupingKey: null,
    fieldsMap: Object.fromEntries(
      Object.entries(equipmentResourceDefinition.fields)
        .filter(([, field]) => field.table?.filterable)
        .map(([key]) => [key, new UIFilterInfo()]),
    ),
  })

  public workflowsFilters = observable<IFilters>({
    searchKey: '',
    groupingKey: LogisticsGroupingOption.TYPE,
    fieldsMap: {
      [WorkflowsFilterType.Status]: new UIFilterInfo(),
      [WorkflowsFilterType.Location]: new UIFilterInfo(),
      [WorkflowsFilterType.Equipment]: new UIFilterInfo(),
      [WorkflowsFilterType.Company]: new UIFilterInfo(),
      [WorkflowsFilterType.Type]: new UIFilterInfo(),
      [WorkflowsFilterType.ResponsibleContact]: new UIFilterInfo(),
      [WorkflowsFilterType.Category]: new UIFilterInfo(),
      [WorkflowsFilterType.Project]: new UIFilterInfo(),
      [WorkflowsFilterType.Attachment]: new UIFilterInfo(),
    },
  })

  public materialFilters = observable<IFilters>({
    searchKey: '',
    groupingKey: MaterialsGroupingOption.NONE,
    fieldsMap: {
      [MaterialsFilterType.MATERIAL]: new UIFilterInfo(),
      [MaterialsFilterType.COMPANY]: new UIFilterInfo(),
      [MaterialsFilterType.STATUS]: new UIFilterInfo(),
      [MaterialsFilterType.PLANNED_INSTALL_LOCATION]: new UIFilterInfo(),
      [MaterialsFilterType.PLANNED_DELIVERY_LOCATION]: new UIFilterInfo(),
      [MaterialsFilterType.CURRENT_LOCATION]: new UIFilterInfo(),
    },
  })

  public scanStationFilters = observable<IFilters>({
    searchKey: '',
    groupingKey: ScanStationGroupingOption.DAY,
    fieldsMap: {
      [ScanStationFilterType.SCANNER]: new UIFilterInfo(),
      [ScanStationFilterType.COMPANY]: new UIFilterInfo(),
    },
  })

  public formReportFilters = observable<IFilters>({
    searchKey: '',
    groupingKey: FormReportGroupingOption.NONE,
    fieldsMap: {
      [FormReportFilterType.COMPANY]: new UIFilterInfo(),
      [FormReportFilterType.STATUS]: new UIFilterInfo(),
      [FormReportFilterType.LOCATION]: new UIFilterInfo(),
      [FormReportFilterType.EQUIPMENT]: new UIFilterInfo(),
    },
  })

  public teamMembersFilters = observable<IFilters>({
    searchKey: '',
    fieldsMap: {
      [MemberFilterType.AccountType]: new UIFilterInfo(),
      [MemberFilterType.InviteStatus]: new UIFilterInfo(),
    },
  })

  public sitemapFilters = observable<IFilters>({
    searchKey: '',
    fieldsMap: {
      [SitemapFilterType.Items]: new UIFilterInfo(),
      [SitemapFilterType.Tags]: new UIFilterInfo(),
    },
    groupingKey: GroupingOption.Lbs,
  })

  public subscribeUsersFilters = observable<IFilters>({
    searchKey: '',
    fieldsMap: {
      OnlyMe: new UIFilterInfo(),
      OnlyMyCompany: new UIFilterInfo(),
    },
    groupingKey: SubscribeUsersSelectorGroupingOption.COMPANY,
  })

  public notificationFilters = observable<IFilters>({
    searchKey: '',
    groupingKey: 'date',
  })

  public globeFilters = observable<IFilters>({
    searchKey: '',
  })

  @observable public timestampToUpdatingDeliveriesIdsMap: {
    [key: number]: string[]
  } = {}

  @observable public timestampToEditingDeliveriesIdsMap: {
    [key: number]: string[]
  } = {}

  public displayedContentObjectId: string
  @observable public sideBarState = BarState.Default

  @observable public deliveryFiltersSettings = new DeliveryFiltersSettingState()
  @observable public deliveryReportFiltersSettings =
    new DeliveryFiltersSettingState()
  @observable
  public attachmentsFiltersSettings = new AttachmentsFiltersSettingState()

  @observable public materialFiltersSettings =
    new MaterialsFiltersSettingState()

  @observable public scanStationFilterSettings =
    new ScanStationFiltersSettingState()
  @observable public formReportFilterSettings =
    new FormReportFiltersSettingState()

  public constructor() {
    super()

    makeObservable(this)
  }

  public updatePresentationSettings(_source: IPresentationSettings) {
    const source =
      _source && !isEmptyObj(_source)
        ? this.getSettingsCopy(_source)
        : this.getDefaultPresentationSettings()
    const { screens, inactivityDuration } = source
    source.inactivityDuration =
      inactivityDuration || defaultPresentationInactivityDuration
    source.screens = screens || defaultPresentationScreens

    if (screens) {
      defaultPresentationScreens.forEach(defaultScreen => {
        if (!screens?.some(({ key }) => key === defaultScreen.key)) {
          source.screens.push(
            Object.assign({}, defaultScreen, { order: screens.length }),
          )
        }
      })
    }

    this.presentationSettings = source
  }

  public getDefaultPresentationSettings(): IPresentationSettings {
    return {
      screens: defaultPresentationScreens,
      inactivityDuration: defaultPresentationInactivityDuration,
      updatedAt: null,
      updatedBy: null,
      projectId: this.activeProject.id,
      id: null,
    }
  }

  @action.bound
  public hideChat() {
    this.activeSideBarComponent = SidebarComponentName.None
  }

  @action.bound
  public openChat() {
    this.activeSideBarComponent = SidebarComponentName.Chat
  }

  @action.bound
  public toggleChat() {
    this.activeSideBarComponent =
      this.activeSideBarComponent === SidebarComponentName.Chat
        ? SidebarComponentName.None
        : SidebarComponentName.Chat
  }

  @action.bound
  public toggleSideUserDirectory() {
    this.activeSideBarComponent =
      this.activeSideBarComponent === SidebarComponentName.UserDirectory
        ? SidebarComponentName.None
        : SidebarComponentName.UserDirectory
  }

  private getSettingsCopy({
    inactivityDuration,
    screens,
    updatedAt,
    updatedBy,
    projectId,
    id,
  }: IPresentationSettings) {
    return {
      screens: copyObjectArray(screens),
      inactivityDuration,
      updatedAt,
      updatedBy,
      projectId,
      id,
    }
  }
}

interface IDesktopFilters extends IFilters, ISharedFilters {
  appliedCustomFilterId: string
  showEmptyBands: boolean
  locationsMap: {
    [filterType: string]: UIFilterInfo
  }
  selectedBandsOption: IBandOption
  selectedDeliveryBandsOption: IBandOption
  selectedDeliveryReportBandsOption: IBandOption
  selectedMaterialBandsOption: IBandOption
}
